import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { Observable, of, forkJoin } from 'rxjs';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { User } from '../../../../models/user.model';
import { UserService } from '../../../../../services/user-service.service';
import { AppService } from '../../../../../services/app.service';
import { PushNotificationService } from '../../../../../services/push-notification-service';
import { UserProfileService } from '../../../../../services/user-profile.service';
import { ProfilesService } from '../../../profiles/profiles.service';
import { ServiceStatus } from '../../service-status.enum';
import { RtI18nDirective } from '../../runtime-translation.directive';
import { CommunicationConsentService, CommunicationConsentItem } from '../../../../../services/communication-consent.service';
import { CommunicationPreferenceTogglesComponent } from '../../communication-preferences/communication-preference-toggles.component';
import { CommunicationDigestComponent } from '../../communication-preferences/communication-digest.component';
import { CommunicationConsentFieldsComponent } from '../../communication-preferences/communication-consent-fields.component';

/**
 * Dialog shown to a guardian (driven by CommunicationConsentService.hasMissingConsent) whenever they
 * have a current-year school with no consent on file, so they set their communication preferences
 * before entering the app. It mirrors the profile page's Communication Preferences section and adds
 * per-school consent checkboxes (Marketing / Operations / an explicit decline) that persist to
 * Core.CommunicationConsent, keyed by phone number + school. Save is disabled until every school has
 * an explicit answer; the decline box is UI-only (a declined school is saved with both consents
 * false). It can only be dismissed by saving — the host opens it via AdaptiveContainerService with
 * disableClose: true (centered dialog on desktop, bottom sheet on mobile) and there is no
 * close/cancel button.
 */
@Component({
  selector: 'app-communication-preferences-modal',
  standalone: true,
  templateUrl: './communication-preferences-modal.component.html',
  styleUrl: './communication-preferences-modal.component.scss',
  imports: [
    MatDialogModule, MatProgressSpinnerModule, RtI18nDirective,
    CommunicationPreferenceTogglesComponent, CommunicationDigestComponent, CommunicationConsentFieldsComponent
  ]
})
export class CommunicationPreferencesModalComponent implements OnInit {
  readonly ServiceStatus = ServiceStatus;
  saveStatus = ServiceStatus.unset;
  isNativePlatform = Capacitor.isNativePlatform();
  currentUserProfile!: User;
  // One row per school the guardian is associated with, each with Marketing/Operations consent.
  consentSchools: CommunicationConsentItem[] = [];
  // Set when the footer hint is clicked; lights up the inline error under each unanswered school
  // (each one clears as it gets answered).
  showConsentError = false;
  @ViewChild('consentSection') private consentSection?: ElementRef<HTMLElement>;
  @ViewChild(CommunicationConsentFieldsComponent) private consentFields?: CommunicationConsentFieldsComponent;
  private _deviceInfo: any | null = null;

  prefsForm = this.fb.group({
    directMessageEmail: [false],
    directMessageText: [false],
    directMessagePush: [{ value: false, disabled: true }],
    directMessageToast: [true],
    postEmail: [false],
    postText: [false],
    postPush: [{ value: false, disabled: true }],
    postPhone: [true],
    criticalAlertPhone: [true],
    enableDigest: [false],
    digestTimeDate: [{ value: null as Date | null, disabled: true }, Validators.required],
    digestTime: ['' as string | null]
  });

  constructor(
    public dialogRef: MatDialogRef<CommunicationPreferencesModalComponent>,
    private fb: FormBuilder,
    private userService: UserService,
    private appService: AppService,
    private pushNotificationService: PushNotificationService,
    private userProfileService: UserProfileService,
    private profileService: ProfilesService,
    private consentService: CommunicationConsentService
  ) {}

  async ngOnInit(): Promise<void> {
    this.currentUserProfile = this.userService.CurrentUserProfile!;
    const prefs = this.currentUserProfile?.NotificationPrefs;

    let digestAsDate: Date | null = null;
    if (prefs?.DigestTime) {
      const [hours, minutes] = prefs.DigestTime.split(':').map(Number);
      digestAsDate = new Date();
      digestAsDate.setHours(hours, minutes, 0, 0);
    }

    this.prefsForm.patchValue({
      directMessageEmail: prefs?.NotifyMessageByEmail ?? false,
      directMessageText: prefs?.NotifyMessageByText ?? false,
      directMessagePush: prefs?.NotifyMessageByPush ?? false,
      directMessageToast: prefs?.NotifyMessageByToast ?? true,
      postEmail: prefs?.NotifyPostByEmail ?? false,
      postText: prefs?.NotifyPostByText ?? false,
      postPush: prefs?.NotifyPostByPush ?? false,
      postPhone: prefs?.NotifyPostByPhone ?? true,
      criticalAlertPhone: prefs?.NotifyCriticalAlertByPhone ?? true,
      enableDigest: !!prefs?.DigestTime,
      digestTime: prefs?.DigestTime ?? null,
      digestTimeDate: digestAsDate
    });

    // Keep the digest time control enabled/required only while the digest is enabled (mirrors profile).
    this.prefsForm.get('enableDigest')!.valueChanges.subscribe(enabled => {
      const timeControl = this.prefsForm.get('digestTimeDate')!;
      if (enabled) {
        timeControl.enable({ emitEvent: false });
        if (this.prefsForm.get('digestTimeDate')?.value == null) {
          const defaultDate = new Date();
          defaultDate.setHours(17, 0, 0, 0);
          this.prefsForm.get('digestTimeDate')?.setValue(defaultDate);
        }
      } else {
        timeControl.disable({ emitEvent: false });
        timeControl.setValue(null, { emitEvent: false });
        this.prefsForm.get('digestTime')?.setValue(null);
      }
      timeControl.updateValueAndValidity();
    });

    // Mirror the picked time into the "HH:mm" string the backend stores.
    this.prefsForm.get('digestTimeDate')!.valueChanges.subscribe((value: Date | null) => {
      if (value instanceof Date) {
        const hours = value.getHours().toString().padStart(2, '0');
        const minutes = value.getMinutes().toString().padStart(2, '0');
        this.prefsForm.get('digestTime')!.setValue(`${hours}:${minutes}`);
      } else if (value == null) {
        this.prefsForm.get('digestTime')!.setValue(null);
      }
    });

    if (prefs?.DigestTime) {
      this.prefsForm.get('digestTimeDate')?.enable();
    }

    this.updatePushControlsDisabledState();

    // Load the guardian's schools + any saved consent for the per-school agreement checkboxes.
    this.consentService.getGuardianConsents().subscribe({
      next: (items) => { this.consentSchools = items ?? []; },
      error: () => { this.consentSchools = []; }
    });

    // Gather device info so push prefs can be registered/unregistered on save (mirrors profile).
    this._deviceInfo = await this.userProfileService.getUserCurrentMobileDeviceInformation();
    if (this._deviceInfo) {
      this._deviceInfo.userId = this.currentUserProfile.Id;
    }
    this.currentUserProfile.PushNotificationDevice = this._deviceInfo;

    if (this.isNativePlatform) {
      this.pushNotificationService.token$.subscribe(token => {
        if (token && this._deviceInfo) {
          this._deviceInfo.token = token;
          this.currentUserProfile.PushNotificationDevice = this._deviceInfo;
        }
      });
    }
  }

  get systemPushPermissionGranted(): boolean {
    return this.appService.getSystemPushPermissionGranted;
  }

  updatePushControlsDisabledState(): void {
    if (this.isNativePlatform && this.systemPushPermissionGranted) {
      this.prefsForm.get('directMessagePush')?.enable();
      this.prefsForm.get('postPush')?.enable();
    } else {
      this.prefsForm.get('directMessagePush')?.disable();
      this.prefsForm.get('postPush')?.disable();
    }
  }

  canSave(): boolean {
    return this.prefsForm.valid && this.allConsentAnswered() && this.saveStatus !== ServiceStatus.loading;
  }

  allConsentAnswered(): boolean {
    // Before the consent section has rendered, only an empty school list counts as answered — this
    // matches what the fields component will report once it exists, so the binding stays stable.
    return this.consentFields?.allAnswered() ?? this.consentSchools.length === 0;
  }

  /** Footer hint click: light up the unanswered schools and bring them into view. */
  scrollToConsent(): void {
    this.showConsentError = true;
    this.consentSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async save(): Promise<void> {
    if (!this.canSave()) return;

    const prefs = this.currentUserProfile.NotificationPrefs;
    if (!prefs) {
      this.saveStatus = ServiceStatus.error;
      return;
    }

    this.saveStatus = ServiceStatus.loading;

    const f = this.prefsForm.getRawValue();
    prefs.NotifyMessageByEmail = f.directMessageEmail ?? false;
    prefs.NotifyMessageByText = f.directMessageText ?? false;
    prefs.NotifyMessageByPush = f.directMessagePush ?? false;
    prefs.NotifyMessageByToast = f.directMessageToast ?? false;
    prefs.NotifyPostByEmail = f.postEmail ?? false;
    prefs.NotifyPostByText = f.postText ?? false;
    prefs.NotifyPostByPush = f.postPush ?? false;
    prefs.NotifyPostByPhone = f.postPhone ?? true;
    prefs.NotifyCriticalAlertByPhone = f.criticalAlertPhone ?? true;
    prefs.DigestTime = f.enableDigest ? (f.digestTime ?? null) : null;

    await this.setPushNotificationSettings();

    // Persist the notification-preference toggles and the per-school consents together.
    forkJoin([
      this.getUpdater(),
      this.consentService.saveGuardianConsents(this.consentSchools)
    ]).subscribe({
      next: async () => {
        await this.userService.setCurrentUserProfile(this.currentUserProfile);
        this.saveStatus = ServiceStatus.loaded;
        this.dialogRef.close();
      },
      error: () => {
        this.saveStatus = ServiceStatus.error;
      }
    });
  }

  private getUpdater(): Observable<boolean> {
    switch (this.currentUserProfile.UserType) {
      case 'Staff':    return this.profileService.updateStaffProfileAysnc(this.currentUserProfile);
      case 'Guardian': return this.profileService.updateGuardianProfileAysnc(this.currentUserProfile);
      case 'Student':  return this.profileService.updateStudentProfileAysnc(this.currentUserProfile);
      default:         return of(false);
    }
  }

  private async setPushNotificationSettings(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      if (this.currentUserProfile.NotificationPrefs.NotifyMessageByPush || this.currentUserProfile.NotificationPrefs.NotifyPostByPush) {
        await this.pushNotificationService.registerPushNotifications();
      } else {
        await this.pushNotificationService.unregisterPushNotifications();
      }
    }

    if (!this.currentUserProfile.PushNotificationDevice) {
      this.currentUserProfile.PushNotificationDevice = this._deviceInfo;
    }
  }
}
